import { useEntity } from '@/hooks/queries/useEntityQuery';
import { roomServiceAdapter } from '@/services/adapters/roomServiceAdapter';
import { Room } from '@/types/floorPlanViewer';
import { useMemo } from 'react';

export function useRoomsStandardized(floorPlanId: string) {
  const {
    data,
    isLoading,
    error,
    refetch,
    create,
    update,
    delete: deleteRoom,
    ...mutations
  } = useEntity<Room>('rooms', floorPlanId, roomServiceAdapter);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data) return { totalRooms: 0, totalArea: 0, roomTypeStats: {} };

    const totalRooms = data.length;
    const totalArea = data.reduce((sum, room: any) => sum + (room.area || 0), 0);

    const roomTypeStats = data.reduce(
      (acc, room: any) => {
        const type = room.room_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return { totalRooms, totalArea, roomTypeStats };
  }, [data]);

  // Enhanced create function with floor plan assignment
  const createRoom = async (roomData: any, options?: { showSuccessToast?: boolean }) => {
    const roomWithFloorPlan = {
      ...roomData,
      floor_plan_id: floorPlanId,
    };
    return create(roomWithFloorPlan, options);
  };

  // Enhanced update function
  const updateRoom = async (id: string, updates: any, options?: { showSuccessToast?: boolean }) => {
    return update({ id, updates, showSuccessToast: options?.showSuccessToast });
  };

  // Bulk operations
  const updateRoomTypes = async (updates: { id: string; room_type: string }[]) => {
    const promises = updates.map(({ id, room_type }) =>
      updateRoom(id, { room_type }, { showSuccessToast: false }),
    );
    return Promise.all(promises);
  };

  const updateRoomAreas = async (updates: { id: string; area: number }[]) => {
    const promises = updates.map(({ id, area }) =>
      updateRoom(id, { area }, { showSuccessToast: false }),
    );
    return Promise.all(promises);
  };

  return {
    // Data
    rooms: data || [],

    // Loading states
    isLoading,
    error,

    // Mutation states
    isCreating: mutations.isCreating,
    isUpdating: mutations.isUpdating,
    isDeleting: mutations.isDeleting,

    // Operations
    createRoom,
    updateRoom,
    deleteRoom,
    refetch,

    // Bulk operations
    updateRoomTypes,
    updateRoomAreas,

    // Statistics
    totalRooms: statistics.totalRooms,
    totalArea: statistics.totalArea,
    getRoomTypeStats: () => statistics.roomTypeStats,
  };
}
